import { useEffect } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'

import { CssUnit } from '@/types'
import { colors, fontWeights } from '@/styles'
import useSessionComplete from '@/hooks/use-session-complete'
import { ChatIcon } from '@/components/icons'
import Required from '@/components/required-indicator'

import { useStore } from '../../lib/redux'

const MobileIndicator = styled(Required)<{
  top: CssUnit
  right: CssUnit
  left: CssUnit
}>`
  position: absolute;
  top: ${props => props.top};
  right: ${props => props.right};
  left: ${props => props.left};
`

const Wrapper = styled.div<{
  showCount: boolean
  marginLeft: CssUnit
  marginRight: CssUnit
}>`
  ${props =>
    props.showCount &&
    `
    width: 31px;
    height: 20px;
  `}
  position: relative;
  margin-left: ${props => props.marginLeft};
  margin-right: ${props => props.marginRight};
`

const Balloon = styled.span<{
  top: CssUnit
  right: CssUnit
  left: CssUnit
  large: boolean
}>`
  position: absolute;
  top: ${props => props.top};
  right: ${props => props.right};
  left: ${props => props.left};
  background-color: ${colors.red64};
  border-radius: 15px;
  height: 16px;
  min-width: 16px;
  line-height: 16px;
  text-align: center;
  color: ${colors.white};
  font-size: 10px;
  font-weight: ${fontWeights.medium};
  padding: 0 3px;
  z-index: 1;

  ${props =>
    props.large &&
    `
      height: 24px;
      min-width: 24px;
      line-height: 24px;
      font-size: 14px;
      padding: 0 5px;
  `}
`

const truncateNumber = (n: number) => (n > 99 ? '99+' : n.toString())

interface Props {
  showIcon?: boolean
  showCount?: boolean
  top?: CssUnit
  right?: CssUnit
  left?: CssUnit
  marginLeft?: CssUnit
  marginRight?: CssUnit
  large?: boolean
}

const ChatNotification: React.FC<Props> = ({
  showIcon = false,
  showCount = true,
  top = 0,
  right = 'unset',
  left = 'unset',
  marginLeft = 0,
  marginRight = 0,
  large = false,
  ...rest
}) => {
  const store = useStore()
  const dispatch = useDispatch()
  const unreadCount = useSelector(store.select.chat.unreadMessages)
  const isLoggedIn = useSessionComplete()
  useEffect(() => {
    if (isLoggedIn) dispatch.chat.checkUnreadMessages()
  }, [dispatch.chat, isLoggedIn])

  return (
    <Wrapper
      marginLeft={marginLeft}
      marginRight={marginRight}
      showCount={showCount}
      {...rest}
    >
      {showIcon && <ChatIcon width={31} height={25} />}
      {!!unreadCount &&
        (showCount ? (
          <Balloon large={large} top={top} right={right} left={left}>
            {truncateNumber(unreadCount)}
          </Balloon>
        ) : (
          <MobileIndicator top={top} right={right} left={left} />
        ))}
    </Wrapper>
  )
}

export default ChatNotification
